import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Handler from "../../../Helpers/Handler";
import { formatDateTime } from "../../../Helpers/Utils";
import { StatusPill } from "../../../Components/Needed";
import ListLayout from "../../../Components/List";
import { SERVICE_TYPES, SERVICE_STATUSES, SERVICE_CENTER_TYPES, serviceStatusVariant } from "../shared/vehicleFleetConstants";
import useListParams from "../../../Helpers/useListParams";

// Global cross-vehicle service ops view (serviceList) - a per-vehicle
// history also appears embedded in each car/bike's own detail page
// (VehicleServicePanel), this is the "all of them" list for ops to triage
// from. `status` here is Service.status (true only once Closed), separate
// from serviceStatus which is the Initiated/Started/Closed workflow stage.
const INITIAL_FILTERS = {
    search: "", searchKey: "serviceName", count: 10,
    serviceType: "", serviceStatus: "", serviceCenterType: "", status: "",
    sortBy: "createdAt", sort: false,
};

const SEARCH_KEYS = [{ key: "serviceName", value: "Service name" }];

const FILTER_ITEMS = {
    dropdown: [
        { key: "serviceType", name: "Type", options: [{ key: "", name: "All" }, ...SERVICE_TYPES.map((t) => ({ key: t, name: t }))] },
        { key: "serviceStatus", name: "Stage", options: [{ key: "", name: "All" }, ...SERVICE_STATUSES.map((s) => ({ key: s, name: s }))] },
        { key: "serviceCenterType", name: "Center type", options: [{ key: "", name: "All" }, ...SERVICE_CENTER_TYPES.map((t) => ({ key: t, name: t }))] },
        { key: "status", name: "Closed", options: [{ key: "", name: "All" }, { key: true, name: "Yes" }, { key: false, name: "No" }] },
    ],
};

const ServiceList = () => {
    const navigate = useNavigate();
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilters } = useListParams(INITIAL_FILTERS);
    const [data, setData] = useState({ list: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({
            method: "post",
            url: `/staff/service/list/${page - 1}`,
            params: { count: filters.count },
            data: {
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.serviceType ? { serviceType: filters.serviceType } : {}),
                ...(filters.serviceStatus ? { serviceStatus: filters.serviceStatus } : {}),
                ...(filters.serviceCenterType ? { serviceCenterType: filters.serviceCenterType } : {}),
                ...(filters.status !== "" ? { status: filters.status } : {}),
            },
        });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0 });
        } else {
            setData({ list: [], total: 0 });
            setError(response?.data?.message || "No data found");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters.count, filters.search, filters.serviceType, filters.serviceStatus, filters.serviceCenterType, filters.status]);

    useEffect(() => { load(); }, [load]);

    const onFilterChange = (key, value) => {
        if (key !== "count") setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resetFilter = () => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const columns = [
        { key: "serviceName", name: "Service", mandatory: true },
        { key: "vehicleType", name: "Vehicle", render: (row) => <span className="text-capitalize">{row.vehicleType} - {row.vehicleId}</span> },
        { key: "serviceType", name: "Type" },
        { key: "serviceStatus", name: "Stage", render: (row) => <span className={`badge rounded-pill ${serviceStatusVariant(row.serviceStatus)}`}>{row.serviceStatus}</span> },
        { key: "status", name: "Closed", render: (row) => <StatusPill active={row.status} activeText="Closed" inactiveText="Open" /> },
        { key: "createdAt", name: "Created", render: (row) => formatDateTime(row.createdAt) },
    ];

    return (
        <ListLayout
            title="Services"
            description="Cross-fleet service records - registration through start, close-out and cost, across every car and bike."
            data={data}
            loading={loading}
            error={error}
            onRetry={load}
            onRefresh={load}
            table={columns}
            page={page}
            setPageNo={setPage}
            pageSize={filters.count}
            onPageSizeChange={(size) => onFilterChange("count", size)}
            filters={filters}
            initialFilters={INITIAL_FILTERS}
            filterItems={FILTER_ITEMS}
            onFilterChange={onFilterChange}
            resetFilter={resetFilter}
            searchKeys={SEARCH_KEYS}
            searchPlaceholder="Search by service name..."
            onRowClick={(row) => navigate(`/vehicle-fleet/services/${row.serviceId}`)}
        />
    );
};

export default ServiceList;
